import { queryOptions, useQuery } from '@tanstack/react-query';
import { api } from '@/lib/axios';
import { API_ROUTES, QUERY_KEYS } from '@/config/constants';
import type { CountryCarriageAvailability, CountryDto } from '@/types/countries';

const PING_CO_CTS_ON_DEMAND = 'ondemand';
const PING_CO_CTS_ON_REQUEST = 'onrequest';

/**
 * The wire shape carries the carriage mode as one nullable enum string; every consumer wants
 * the two booleans the Country Admin table edits, so normalise once here. A row with no
 * country code cannot be matched against `REGIONS` and is dropped.
 */
const toCarriageAvailability = (countries: CountryDto[]): CountryCarriageAvailability[] =>
  countries
    .filter((country) => country.countryCode != null && country.countryCode.length > 0)
    .map((country) => {
      const mode = country.pingCoCts?.toLowerCase() ?? '';
      return {
        countryCode: (country.countryCode ?? '').toUpperCase(),
        byoc: country.byoc,
        onDemand: mode === PING_CO_CTS_ON_DEMAND,
        onRequest: mode === PING_CO_CTS_ON_REQUEST,
      };
    });

const fetchCountries = async (): Promise<CountryCarriageAvailability[]> => {
  const { data } = await api.get<CountryDto[]>(API_ROUTES.countries);
  return toCarriageAvailability(data);
};

export const getCountriesQueryOptions = () =>
  queryOptions({
    queryKey: QUERY_KEYS.countries,
    queryFn: fetchCountries,
  });

export const useCountries = () => useQuery(getCountriesQueryOptions());
